import React, { useState } from 'react';
import { AssetStatus } from '../types';

interface Props {
  isVisible: boolean;
  onClose: () => void;
  assets: AssetStatus[];
  selectedAssets: string[];
}

interface DownloadProgress {
  assetId: string;
  assetName: string;
  status: 'pending' | 'downloading' | 'completed' | 'failed';
  progress: number;
  url?: string;
  error?: string;
}

const DownloadManager: React.FC<Props> = ({ isVisible, onClose, assets, selectedAssets }) => {
  const [downloads, setDownloads] = useState<DownloadProgress[]>([]);
  const [isDownloading, setIsDownloading] = useState(false);

  if (!isVisible) return null;

  const selectedAssetData = assets.filter(asset =>
    selectedAssets.includes(asset.assetId) && asset.status === 'completed' && asset.imageUrl
  );

  const handleStartDownload = async () => {
    if (selectedAssetData.length === 0) return;

    setIsDownloading(true);

    // 初始化下载进度
    const initialDownloads: DownloadProgress[] = selectedAssetData.map(asset => ({
      assetId: asset.assetId,
      assetName: asset.assetName,
      status: 'pending',
      progress: 0,
    }));

    setDownloads(initialDownloads);

    // 批量下载
    for (let i = 0; i < selectedAssetData.length; i++) {
      const asset = selectedAssetData[i];

      try {
        // 更新状态为下载中
        setDownloads(prev => prev.map(d =>
          d.assetId === asset.assetId
            ? { ...d, status: 'downloading', progress: 10 }
            : d
        ));

        // 下载图片
        const response = await fetch(asset.imageUrl!);

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        // 模拟下载进度
        setDownloads(prev => prev.map(d =>
          d.assetId === asset.assetId
            ? { ...d, progress: 50 }
            : d
        ));

        const blob = await response.blob();

        // 创建下载链接
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;

        // 设置文件名
        const fileExtension = asset.imageUrl!.split('.').pop() || 'png';
        const fileName = `${asset.assetName}.${fileExtension}`;
        link.download = fileName;

        // 触发下载
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        // 释放 URL
        window.URL.revokeObjectURL(url);

        // 更新为完成状态
        setDownloads(prev => prev.map(d =>
          d.assetId === asset.assetId
            ? { ...d, status: 'completed', progress: 100, url }
            : d
        ));

        // 添加延迟避免浏览器阻止多个下载
        if (i < selectedAssetData.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 500));
        }

      } catch (error) {
        console.error(`下载失败 ${asset.assetName}:`, error);

        setDownloads(prev => prev.map(d =>
          d.assetId === asset.assetId
            ? {
                ...d,
                status: 'failed',
                progress: 0,
                error: error instanceof Error ? error.message : '下载失败'
              }
            : d
        ));
      }
    }

    setIsDownloading(false);
  };

  const handleRetryFailed = () => {
    const failedAssets = downloads.filter(d => d.status === 'failed');
    if (failedAssets.length === 0) return;

    // 重置失败的下载
    setDownloads(prev => prev.map(d =>
      d.status === 'failed'
        ? { ...d, status: 'pending', progress: 0, error: undefined }
        : d
    ));

    // 重新开始下载
    handleStartDownload();
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pending':
        return '⏳';
      case 'downloading':
        return '⬇️';
      case 'completed':
        return '✅';
      case 'failed':
        return '❌';
      default:
        return '❓';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'text-yellow-600 bg-yellow-100';
      case 'downloading':
        return 'text-blue-600 bg-blue-100';
      case 'completed':
        return 'text-green-600 bg-green-100';
      case 'failed':
        return 'text-red-600 bg-red-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  const completedCount = downloads.filter(d => d.status === 'completed').length;
  const failedCount = downloads.filter(d => d.status === 'failed').length;
  const totalCount = downloads.length;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] flex flex-col">
        {/* 头部 */}
        <div className="flex justify-between items-center p-6 border-b">
          <h2 className="text-xl font-bold text-gray-800">
            📥 批量下载管理器
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            disabled={isDownloading}
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* 内容区域 */}
        <div className="flex-1 overflow-y-auto p-6">
          {selectedAssetData.length === 0 ? (
            <div className="text-center py-8">
              <div className="text-gray-400 mb-4">
                <svg className="mx-auto h-16 w-16" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-800 mb-2">没有选择素材</h3>
              <p className="text-gray-600">请先在素材画廊中选择要下载的素材</p>
            </div>
          ) : (
            <>
              {/* 下载概览 */}
              <div className="mb-6">
                <div className="flex justify-between items-center mb-4">
                  <span className="text-sm font-medium text-gray-700">
                    选中素材: {selectedAssetData.length} 个
                  </span>
                  {totalCount > 0 && (
                    <span className="text-sm text-gray-600">
                      已完成: {completedCount}/{totalCount}
                      {failedCount > 0 && ` (失败: ${failedCount})`}
                    </span>
                  )}
                </div>

                {totalCount > 0 && (
                  <div className="w-full bg-gray-200 rounded-full h-2 mb-4">
                    <div
                      className="bg-green-600 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${(completedCount / totalCount) * 100}%` }}
                    ></div>
                  </div>
                )}
              </div>

              {/* 下载列表 */}
              {downloads.length > 0 && (
                <div className="space-y-3 mb-6">
                  <h3 className="text-sm font-medium text-gray-700 mb-3">下载进度</h3>
                  {downloads.map((download) => (
                    <div key={download.assetId} className="border rounded-lg p-4">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center">
                          <span className="text-lg mr-2">{getStatusIcon(download.status)}</span>
                          <div>
                            <h4 className="font-medium text-gray-800">{download.assetName}</h4>
                            {download.error && (
                              <p className="text-sm text-red-600">{download.error}</p>
                            )}
                          </div>
                        </div>
                        <div className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(download.status)}`}>
                          {download.status === 'pending' && '等待中'}
                          {download.status === 'downloading' && '下载中'}
                          {download.status === 'completed' && '已完成'}
                          {download.status === 'failed' && '失败'}
                        </div>
                      </div>

                      {/* 进度条 */}
                      {(download.status === 'downloading' || download.status === 'completed') && (
                        <div className="mt-2">
                          <div className="w-full bg-gray-200 rounded-full h-1">
                            <div
                              className={`h-1 rounded-full transition-all duration-300 ${
                                download.status === 'completed' ? 'bg-green-600' : 'bg-blue-600'
                              }`}
                              style={{ width: `${download.progress}%` }}
                            ></div>
                          </div>
                          <p className="text-xs text-gray-500 mt-1">{download.progress}%</p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* 素材预览 */}
              <div className="grid grid-cols-3 gap-3">
                {selectedAssetData.map((asset) => (
                  <div key={asset.assetId} className="text-center">
                    <img
                      src={asset.imageUrl}
                      alt={asset.assetName}
                      className="w-full aspect-square object-cover rounded-lg border"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="100" height="100" viewBox="0 0 100 100"><rect width="100" height="100" fill="%23f3f4f6"/><text x="50" y="50" text-anchor="middle" dy="0.3em" font-family="sans-serif" font-size="10" fill="%236b7280">图片错误</text></svg>';
                      }}
                    />
                    <p className="text-xs text-gray-600 mt-1 truncate">{asset.assetName}</p>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        {/* 底部操作 */}
        {selectedAssetData.length > 0 && (
          <div className="border-t p-6 flex justify-between">
            <div className="flex space-x-2">
              {failedCount > 0 && !isDownloading && (
                <button
                  onClick={handleRetryFailed}
                  className="px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition-colors"
                >
                  🔄 重试失败项
                </button>
              )}
            </div>

            <div className="flex space-x-2">
              <button
                onClick={onClose}
                disabled={isDownloading}
                className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isDownloading ? '下载中...' : '关闭'}
              </button>

              {downloads.length === 0 && (
                <button
                  onClick={handleStartDownload}
                  disabled={isDownloading}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isDownloading ? (
                    <span className="flex items-center">
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      下载中...
                    </span>
                  ) : (
                    '📥 开始下载'
                  )}
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DownloadManager;